import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../authContext';
import useSelectedRadio from '../hooks/useSelectedRadio';
import { authFetch } from '../authFetch';
import API_BASE_URL from '../api';
import { useTranslation } from 'react-i18next';
import ExportButton from '../components/ExportButton';

export default function QuartilesLtrServicePage() {
  const { isAuth } = useAuth();
  const { radioId, shortcode, name: stationName } = useSelectedRadio();
  const { t, i18n } = useTranslation('common');

  const serviceName = t('quartiles_ltr.title');
  const exportLocale = resolveLocaleForFormatter(i18n?.language);
  const stationDisplayName = stationName || shortcode || '';
  const headingTitle = stationDisplayName
    ? t('quartiles_ltr.heading', { station: stationDisplayName })
    : serviceName;

  const [date, setDate] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [infoKey, setInfoKey] = useState('');
  const [geoModalOpen, setGeoModalOpen] = useState(false);
  const [geoModalLoading, setGeoModalLoading] = useState(false);
  const [geoModalError, setGeoModalError] = useState('');
  const [geoModalClip, setGeoModalClip] = useState(null); // { start, end, clipName }
  const [geoModalCountries, setGeoModalCountries] = useState([]);

  const [ipModalOpen, setIpModalOpen] = useState(false);
  const [ipModalLoading, setIpModalLoading] = useState(false);
  const [ipModalError, setIpModalError] = useState('');
  const [ipModalCountry, setIpModalCountry] = useState('');
  const [ipModalTotal, setIpModalTotal] = useState(0);
  const [ipModalListeners, setIpModalListeners] = useState([]);

  const exportStrings = React.useMemo(
    () => ({
      serviceName,
      stationLabel: t('quartiles_ltr.station_label'),
      dateLabel: t('quartiles_ltr.date_label_plain'),
      emptyExport: t('quartiles_ltr.export_empty'),
      table: {
        start: t('quartiles_ltr.table.start'),
        end: t('quartiles_ltr.table.end'),
        clips: t('quartiles_ltr.table.clips'),
        impressions: t('quartiles_ltr.table.impressions'),
        q25: t('quartiles_ltr.table.q25'),
        q50: t('quartiles_ltr.table.q50'),
        q75: t('quartiles_ltr.table.q75'),
        q100: t('quartiles_ltr.table.q100'),
      },
    }),
    [serviceName, t]
  );

  // дефолтная дата — сегодня
  useEffect(() => {
    if (!date) {
      const today = new Date();
      setDate(today.toISOString().slice(0, 10));
    }
  }, [date]);

  const fetchDailyQuartiles = useCallback(async () => {
    if (!isAuth || !radioId || !date) return;

    setLoading(true);
    setInfoKey('');
    setBlocks([]);

    try {
      const API = (API_BASE_URL || '').replace(/\/+$/, '');
      const url = new URL(`${API}/radios/${encodeURIComponent(radioId)}/metrics/quartiles-ltr/daily-quartiles`);
      url.searchParams.set('date', date);

      const res = await authFetch(url.toString());

      // ЯВНО проверяем HTTP-статус и логируем тело при ошибке
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        console.error(
          '[Quartiles LTR] HTTP error:',
          res.status,
          res.statusText,
          text
        );
        throw new Error(`HTTP ${res.status}`);
      }

      const data = await res.json();

      if (!data || !Array.isArray(data.items) || !data.items.length) {
        setInfoKey('quartiles_ltr.no_blocks');
        setBlocks([]);
      } else {
        setBlocks(data.items);
      }
    } catch (e) {
      console.error('[Quartiles LTR] daily_quartiles error:', e);
      setInfoKey('network_error');
      setBlocks([]);
    }

    setLoading(false);
  }, [isAuth, radioId, date]);

  const handleOpenGeoModal = useCallback(
    async (block) => {
      if (!isAuth || !radioId || !date) return;

      const start = String(block.start || '');
      const end = String(block.end || '');
      const clipName = joinClips(block.clips);

      setGeoModalClip({ start, end, clipName });
      setGeoModalCountries([]);
      setGeoModalError('');
      setGeoModalOpen(true);
      setGeoModalLoading(true);

      try {
        const API = (API_BASE_URL || '').replace(/\/+$/, '');
        const url = new URL(`${API}/radios/${encodeURIComponent(radioId)}/metrics/quartiles-ltr/clip-geo`);
        url.searchParams.set('date', date);
        url.searchParams.set('clip_start', start);
        url.searchParams.set('clip_end', end);
        url.searchParams.set('min_quartile', '0'); // все impressions

        const res = await authFetch(url.toString());

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.error(
            '[Quartiles LTR] clip_geo HTTP error:',
            res.status,
            res.statusText,
            text
          );
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        const countries = Array.isArray(data?.countries) ? data.countries : [];
        setGeoModalCountries(countries);
      } catch (e) {
        console.error('[Quartiles LTR] clip_geo error:', e);
        setGeoModalError('quartiles_ltr.geo_modal.error');
      } finally {
        setGeoModalLoading(false);
      }
    },
    [isAuth, radioId, date]
  );

  const handleCloseGeoModal = useCallback(() => {
    setGeoModalOpen(false);
  }, []);

  const handleOpenIpModal = useCallback(
    async (country) => {
      if (!isAuth || !radioId || !date || !geoModalClip) return;

      setIpModalCountry(country || '');
      setIpModalError('');
      setIpModalListeners([]);
      setIpModalTotal(0);
      setIpModalOpen(true);
      setIpModalLoading(true);

      try {
        const API = (API_BASE_URL || '').replace(/\/+$/, '');
        const url = new URL(`${API}/radios/${encodeURIComponent(radioId)}/metrics/quartiles-ltr/clip-listeners`);
        url.searchParams.set('date', date);
        url.searchParams.set('clip_start', geoModalClip.start);
        url.searchParams.set('clip_end', geoModalClip.end);
        if (country) {
          url.searchParams.set('country', country);
        }
        url.searchParams.set('quartile', '0'); // все слушатели

        const res = await authFetch(url.toString());

        if (!res.ok) {
          const text = await res.text().catch(() => '');
          console.error(
            '[Quartiles LTR] clip_listeners HTTP error:',
            res.status,
            res.statusText,
            text
          );
          throw new Error(`HTTP ${res.status}`);
        }

        const data = await res.json();
        const listeners = Array.isArray(data?.listeners) ? data.listeners : [];
        setIpModalListeners(listeners);
        setIpModalTotal(
          typeof data?.total_listeners === 'number'
            ? data.total_listeners
            : listeners.length
        );
      } catch (e) {
        console.error('[Quartiles LTR] clip_listeners error:', e);
        setIpModalError('quartiles_ltr.ip_modal.error');
      } finally {
        setIpModalLoading(false);
      }
    },
    [isAuth, radioId, date, geoModalClip]
  );

  const handleCloseIpModal = useCallback(() => {
    setIpModalOpen(false);
  }, []);

  const exportDisabled = !blocks.length || loading;
  const exportButtonStyle = buildPrimaryButtonStyle(exportDisabled);
  const filenameBase = buildFilenameBase('quartiles_ltr', shortcode, date);

  const infoMessage = infoKey ? t(infoKey) : '';
  const descriptionParagraphs = (t('quartiles_ltr.description') || '')
    .split(/\n{2,}/)
    .filter(Boolean);

  if (!radioId) {
    return (
      <div style={widgetStyle}>
        <h2 style={{ marginBottom: 8 }}>{serviceName}</h2>
        <div style={{ color: '#e54848', fontWeight: 600, fontSize: 18 }}>
          {t('no_channel') || 'Оберіть радіостанцію у списку'}
        </div>
      </div>
    );
  }

  return (
    <div style={widgetStyle}>
      <div
        style={{
          width: '100%',
          padding: '8px 12px',
          borderRadius: 8,
          background: '#f8f8f8',
          color: '#1a1a1a',
          fontSize: 17,
          margin: '8px 0 18px 0',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 4 }}>{serviceName}</div>
        <div>
          {descriptionParagraphs.map((paragraph, idx) => (
            <p
              key={idx}
              style={{
                margin:
                  idx === 0
                    ? '0 0 8px 0'
                    : idx === descriptionParagraphs.length - 1
                      ? 0
                      : '0 0 8px 0',
              }}
            >
              {paragraph}
            </p>
          ))}
        </div>
      </div>

      <h2 style={{ marginBottom: 8 }}>{headingTitle}</h2>

      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          gap: ACTION_BUTTON_GAP,
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontWeight: 500 }}>
          {t('quartiles_ltr.date_label')}
        </span>
        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={inputStyle}
        />
        <button onClick={fetchDailyQuartiles} style={showBtn}>
          {t('show')}
        </button>
        <ExportButton
          data={blocks}
          buildDoc={(rows) =>
            buildQuartilesPdfDoc({
              blocks: rows,
              stationName,
              shortcode,
              date,
              strings: exportStrings,
              locale: exportLocale,
            })
          }
          buildWorkbook={(XLSX, rows) =>
            buildQuartilesWorkbook(XLSX, {
              blocks: rows,
              stationName,
              shortcode,
              date,
              strings: exportStrings,
              locale: exportLocale,
            })
          }
          filename={`${filenameBase}.pdf`}
          xlsxFilename={`${filenameBase}.xlsx`}
          label={t('export_pdf')}
          labelXlsx={t('export_excel')}
          style={exportButtonStyle}
          disabled={exportDisabled}
        />
      </div>

      {loading && (
        <div style={{ margin: '18px 0', fontWeight: 500 }}>
          {t('loading') || 'Завантаження...'}
        </div>
      )}
      {infoMessage && !loading && (
        <div style={{ margin: '18px 0', fontWeight: 500 }}>{infoMessage}</div>
      )}

      {!loading && blocks.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse', fontSize: 16 }}
          >
            <thead>
              <tr style={{ background: '#f0f0f0' }}>
                <th style={startEndColumnStyle}>
                  {exportStrings.table.start}
                </th>
                <th style={startEndColumnStyle}>{exportStrings.table.end}</th>
                <th style={thTd}>{exportStrings.table.clips}</th>
                <th style={impressionsHeaderStyle}>
                  {exportStrings.table.impressions}
                </th>
                <th style={thTd}>{exportStrings.table.q25}</th>
                <th style={thTd}>{exportStrings.table.q50}</th>
                <th style={thTd}>{exportStrings.table.q75}</th>
                <th style={thTd}>{exportStrings.table.q100}</th>
              </tr>
            </thead>
            <tbody>
              {blocks.map((block, idx) => {
                const row = mapBlocksForExport(block, exportLocale);

                // определяем, начался ли новый рекламный блок (есть зазор по времени)
                const hasSeparator =
                  idx > 0 &&
                  isNewAdBlock(
                    mapBlocksForExport(blocks[idx - 1], exportLocale),
                    row
                  );

                const cellSeparator = hasSeparator ? blockSeparatorStyle : null;

                return (
                  <tr key={idx}>
                    <td style={{ ...startEndColumnStyle, ...cellSeparator }}>
                      {row.start}
                    </td>
                    <td style={{ ...startEndColumnStyle, ...cellSeparator }}>
                      {row.end}
                    </td>
                    <td style={{ ...thTd, ...cellSeparator }}>
                      {row.clips
                        ? row.clips.split('\n').map((clip, j) => (
                            <span key={j}>
                              {clip}
                              <br />
                            </span>
                          ))
                        : '—'}
                    </td>
                    <td style={{ ...impressionsColumnStyle, ...cellSeparator }}>
                      <button
                        type="button"
                        onClick={() => handleOpenGeoModal(block)}
                        style={{
                          ...impressionsButtonStyle,
                          cursor: row.impressions ? 'pointer' : 'default',
                          textDecoration: row.impressions
                            ? 'underline'
                            : 'none',
                        }}
                        disabled={!row.impressions}
                      >
                        {row.impressionsDisplay}
                      </button>
                    </td>
                    <td
                      style={{
                        ...thTd,
                        textAlign: 'center',
                        ...cellSeparator,
                      }}
                    >
                      {row.q25Display}
                    </td>
                    <td
                      style={{
                        ...thTd,
                        textAlign: 'center',
                        ...cellSeparator,
                      }}
                    >
                      {row.q50Display}
                    </td>
                    <td
                      style={{
                        ...thTd,
                        textAlign: 'center',
                        ...cellSeparator,
                      }}
                    >
                      {row.q75Display}
                    </td>
                    <td
                      style={{
                        ...thTd,
                        textAlign: 'center',
                        ...cellSeparator,
                      }}
                    >
                      {row.q100Display}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {geoModalOpen && (
        <div style={modalOverlayStyle} onClick={handleCloseGeoModal}>
          <div
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={modalHeaderStyle}>
              <div>
                <div style={modalTitleStyle}>
                  {t('quartiles_ltr.geo_modal.title')}
                </div>
                {geoModalClip && (
                  <div style={modalSubtitleStyle}>
                    {geoModalClip.clipName || '—'} ·{' '}
                    {formatTime(geoModalClip.start)}–
                    {formatTime(geoModalClip.end)} · {date}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleCloseGeoModal}
                style={modalCloseButtonStyle}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              {geoModalLoading && (
                <div style={modalInfoStyle}>
                  {t('quartiles_ltr.geo_modal.loading')}
                </div>
              )}

              {geoModalError && !geoModalLoading && (
                <div style={modalErrorStyle}>{t(geoModalError)}</div>
              )}

              {!geoModalLoading &&
                !geoModalError &&
                geoModalCountries &&
                geoModalCountries.length > 0 && (
                  <table style={modalTableStyle}>
                    <thead>
                      <tr>
                        <th style={modalThTdStyle}>
                          {t('quartiles_ltr.geo_modal.columns.country')}
                        </th>
                        <th style={modalThTdStyle}>
                          {t('quartiles_ltr.geo_modal.columns.listeners')}
                        </th>
                        <th style={modalThTdStyle}>
                          {t('quartiles_ltr.geo_modal.columns.ip')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {geoModalCountries.map((c) => (
                        <tr key={c.country || 'unknown'}>
                          <td style={modalThTdStyle}>{c.country || '—'}</td>
                          <td style={modalThTdStyle}>
                            {typeof c.impressions === 'number'
                              ? c.impressions
                              : c.listeners_ge_quartile ?? '—'}
                          </td>
                        <td style={modalThTdStyle}>
                          <button
                            type="button"
                            style={modalSmallButtonStyle}
                            onClick={() => handleOpenIpModal(c.country)}
                          >
                            {t('quartiles_ltr.geo_modal.ip_button')}
                          </button>
                        </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

              {!geoModalLoading &&
                !geoModalError &&
                (!geoModalCountries || !geoModalCountries.length) && (
                  <div style={modalInfoStyle}>
                    {t('quartiles_ltr.geo_modal.empty')}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {ipModalOpen && (
        <div style={modalOverlayStyle} onClick={handleCloseIpModal}>
          <div
            style={modalContentStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={modalHeaderStyle}>
              <div>
                <div style={modalTitleStyle}>
                  {t('quartiles_ltr.ip_modal.title')}
                </div>
                {geoModalClip && (
                  <div style={modalSubtitleStyle}>
                    {geoModalClip.clipName || '—'} ·{' '}
                    {formatTime(geoModalClip.start)}–
                    {formatTime(geoModalClip.end)} · {date}
                  </div>
                )}
                {ipModalCountry && (
                  <div style={modalSubtitleStyle}>
                    {t('quartiles_ltr.ip_modal.country_total', {
                      country: ipModalCountry,
                      total: ipModalTotal,
                    })}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={handleCloseIpModal}
                style={modalCloseButtonStyle}
              >
                ×
              </button>
            </div>

            <div style={modalBodyStyle}>
              {ipModalLoading && (
                <div style={modalInfoStyle}>
                  {t('quartiles_ltr.ip_modal.loading')}
                </div>
              )}

              {ipModalError && !ipModalLoading && (
                <div style={modalErrorStyle}>{t(ipModalError)}</div>
              )}

              {!ipModalLoading &&
                !ipModalError &&
                ipModalListeners &&
                ipModalListeners.length > 0 && (
                  <table style={modalTableStyle}>
                    <thead>
                      <tr>
                        <th style={modalThTdStyle}>
                          {t('quartiles_ltr.ip_modal.columns.ip')}
                        </th>
                        <th style={modalThTdStyle}>
                          {t('quartiles_ltr.ip_modal.columns.max_quartile')}
                        </th>
                        <th style={modalThTdStyle}>
                          {t('quartiles_ltr.ip_modal.columns.seconds')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ipModalListeners.map((item, idx) => (
                        <tr key={`${item.ip || 'ip'}-${idx}`}>
                          <td style={modalThTdStyle}>{maskIp(item.ip)}</td>
                          <td style={modalThTdStyle}>
                            {item.max_quartile ?? '—'}
                          </td>
                          <td style={modalThTdStyle}>
                            {item.listened_secs ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}

              {!ipModalLoading &&
                !ipModalError &&
                (!ipModalListeners || !ipModalListeners.length) && (
                  <div style={modalInfoStyle}>
                    {t('quartiles_ltr.ip_modal.empty')}
                  </div>
                )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const widgetStyle = {
  maxWidth: 1400,
  margin: '40px auto',
  background: '#fff',
  borderRadius: 14,
  padding: 24,
  boxShadow: '0 2px 12px 0 #0001',
};

const inputStyle = {
  marginRight: 8,
  padding: 7,
  borderRadius: 4,
  border: '1px solid #ccc',
  fontSize: 16,
  minWidth: 130,
};

const ACTION_BUTTON_GAP = 12;

const primaryButtonBase = {
  border: 'none',
  padding: '8px 16px',
  borderRadius: 5,
  fontWeight: 600,
  minWidth: 150,
  textAlign: 'center',
};

function buildPrimaryButtonStyle(disabled = false) {
  return {
    ...primaryButtonBase,
    background: disabled ? '#9bbdfd' : '#3867fa',
    color: '#fff',
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}

const showBtn = buildPrimaryButtonStyle(false);

const thTd = {
  padding: '7px 12px',
  border: '1px solid #e0e0e0',
  textAlign: 'left',
  verticalAlign: 'top',
};

const COLUMN_WIDTH_INCREMENT = 'calc((98% / 12) + 1%)';
const startEndColumnStyle = { ...thTd, width: COLUMN_WIDTH_INCREMENT };
const impressionsHeaderStyle = {
  ...thTd,
  width: COLUMN_WIDTH_INCREMENT,
  whiteSpace: 'nowrap',
};
const impressionsColumnStyle = {
  ...thTd,
  width: COLUMN_WIDTH_INCREMENT,
  textAlign: 'center',
  whiteSpace: 'nowrap',
};

const blockSeparatorStyle = {
  borderTop: '3px solid #000',
};

const impressionsButtonStyle = {
  background: 'none',
  border: 'none',
  padding: 0,
  margin: 0,
  font: 'inherit',
};

const modalOverlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.35)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: 16,
  boxSizing: 'border-box',
};

const modalContentStyle = {
  width: 'min(95vw, 640px)',
  maxHeight: '85vh',
  background: '#fff',
  borderRadius: 14,
  boxShadow: '0 12px 32px rgba(0, 0, 0, 0.25)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

const modalHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: '16px 22px',
  background: '#00723a',
  color: '#fff',
  gap: 16,
};

const modalTitleStyle = {
  fontSize: 20,
  fontWeight: 700,
  marginBottom: 2,
  color: '#fff',
};

const modalSubtitleStyle = {
  fontSize: 13,
  color: 'rgba(255, 255, 255, 0.85)',
};

const modalCloseButtonStyle = {
  border: 'none',
  background: 'transparent',
  fontSize: 28,
  lineHeight: 1,
  cursor: 'pointer',
  padding: 0,
  margin: '-4px -8px 0 0',
  color: '#fff',
};

const modalBodyStyle = {
  padding: '20px 22px 24px',
  background: '#fff',
  color: '#1f1f1f',
  fontSize: 15,
  overflowY: 'auto',
  flex: 1,
};

const modalInfoStyle = {
  fontSize: 15,
  textAlign: 'center',
  padding: '12px 0',
};

const modalErrorStyle = {
  fontSize: 15,
  color: '#e54848',
  fontWeight: 600,
  textAlign: 'center',
  padding: '12px 0',
};

const modalTableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 12,
  fontSize: 14,
  borderSpacing: 0,
};

const modalThTdStyle = {
  border: '1px solid #e0e0e0',
  padding: '8px 12px',
  textAlign: 'left',
  background: '#fff',
};

const modalSmallButtonStyle = {
  border: 'none',
  padding: '6px 12px',
  borderRadius: 6,
  background: '#00723a',
  color: '#fff',
  fontSize: 13,
  cursor: 'pointer',
  fontWeight: 600,
};

function buildQuartilesPdfDoc(params = {}) {
  const {
    blocks = [],
    stationName = '',
    shortcode = '',
    date = '',
    strings = {},
    locale = 'uk-UA',
  } = params;

  const rows = (blocks || []).map((block) =>
    mapBlocksForExport(block, locale)
  );
  const tableStrings = strings.table || {};

  const headerRow = [
    { text: tableStrings.start || 'Start', style: 'tableHeader' },
    { text: tableStrings.end || 'End', style: 'tableHeader' },
    { text: tableStrings.clips || 'Clips', style: 'tableHeader' },
    { text: tableStrings.impressions || 'Impressions', style: 'tableHeader' },
    { text: tableStrings.q25 || 'Q25, %', style: 'tableHeader' },
    { text: tableStrings.q50 || 'Q50, %', style: 'tableHeader' },
    { text: tableStrings.q75 || 'Q75, %', style: 'tableHeader' },
    { text: tableStrings.q100 || 'Q100, %', style: 'tableHeader' },
  ];

  const bodyRows = rows.length
    ? rows.map((row) => [
        { text: row.start || '—', style: 'tableCell' },
        { text: row.end || '—', style: 'tableCell' },
        { text: row.clips || '—', style: 'tableCell' },
        {
          text: row.impressionsDisplay,
          style: 'tableCell',
          alignment: 'right',
        },
        { text: row.q25Display, style: 'tableCell', alignment: 'right' },
        { text: row.q50Display, style: 'tableCell', alignment: 'right' },
        { text: row.q75Display, style: 'tableCell', alignment: 'right' },
        { text: row.q100Display, style: 'tableCell', alignment: 'right' },
      ])
    : [
        [
          {
            text: strings.emptyExport || 'No data to export',
            colSpan: 8,
            style: 'emptyCell',
          },
          {},
          {},
          {},
          {},
          {},
          {},
          {},
        ],
      ];

  const stationLine =
    stationName || shortcode
      ? `${strings.stationLabel || 'Station'}: ${
          stationName || shortcode
        }`
      : '';
  const dateLine = `${strings.dateLabel || 'Date:'} ${date || '—'}`;

  const content = [
    {
      text: strings.serviceName || 'Quartiles LTR',
      style: 'title',
      margin: [0, 0, 0, 4],
    },
  ];

  if (stationLine) {
    content.push({ text: stationLine, style: 'subtitle' });
  }
  content.push({ text: dateLine, style: 'subtitle', margin: [0, 0, 0, 12] });
  content.push({
    table: {
      headerRows: 1,
      widths: [60, 60, '*', 'auto', 'auto', 'auto', 'auto', 'auto'],
      body: [headerRow, ...bodyRows],
    },
    layout: 'lightHorizontalLines',
  });

  return {
    pageMargins: [32, 40, 32, 40],
    content,
    defaultStyle: { fontSize: 10, color: '#1f1f1f' },
    styles: {
      title: { fontSize: 18, bold: true, color: '#111' },
      subtitle: { fontSize: 11, margin: [0, 0, 0, 6], color: '#333' },
      tableHeader: {
        fontSize: 10,
        bold: true,
        fillColor: '#f0f0f0',
        margin: [0, 4, 0, 4],
      },
      tableCell: { fontSize: 10 },
      emptyCell: {
        fontSize: 11,
        italics: true,
        color: '#777',
        alignment: 'center',
      },
    },
  };
}

function buildQuartilesWorkbook(XLSX, params = {}) {
  const {
    blocks = [],
    stationName = '',
    shortcode = '',
    date = '',
    strings = {},
    locale = 'uk-UA',
  } = params;

  const rows = (blocks || []).map((block) =>
    mapBlocksForExport(block, locale)
  );
  const tableStrings = strings.table || {};

  const header = [
    tableStrings.start || 'Start',
    tableStrings.end || 'End',
    tableStrings.clips || 'Clips',
    tableStrings.impressions || 'Impressions',
    tableStrings.q25 || 'Q25',
    tableStrings.q50 || 'Q50',
    tableStrings.q75 || 'Q75',
    tableStrings.q100 || 'Q100',
  ];

  // В Excel пишем КОЛИЧЕСТВА слушателей, а не проценты
  const tableRows = rows.map((row) => [
    row.start || '',
    row.end || '',
    row.clips || '',
    row.impressions,
    row.q25Count ?? '',
    row.q50Count ?? '',
    row.q75Count ?? '',
    row.q100Count ?? '',
  ]);

  const stationLine =
    stationName || shortcode
      ? `${strings.stationLabel || 'Station'}: ${
          stationName || shortcode
        }`
      : null;

  const prefixRows = [
    [strings.serviceName || 'Quartiles LTR'],
    stationLine ? [stationLine] : null,
    [`${strings.dateLabel || 'Date:'} ${date || '—'}`],
    [''],
  ].filter(Boolean);

  const aoa = [...prefixRows, header, ...tableRows];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 12 },
    { wch: 12 },
    { wch: 40 },
    { wch: 15 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
    { wch: 10 },
  ];

  if (tableRows.length) {
    const headerRowIndex = prefixRows.length;
    const endRow = headerRowIndex + tableRows.length;
    ws['!autofilter'] = {
      ref: XLSX.utils.encode_range({
        s: { r: headerRowIndex, c: 0 },
        e: { r: endRow, c: header.length - 1 },
      }),
    };
  }

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Quartiles');
  return wb;
}

function mapBlocksForExport(block = {}, locale = 'uk-UA') {
  const { numberFormatter } = createFormatters(locale);

  const impressions = toNumber(block.impressions);

  // Backend уже возвращает КОЛИЧЕСТВА слушателей по квартилям,
  // поэтому трактуем q25/q50/q75/q100 как штуки, а не проценты
  const q25Count =
    block.q25 == null || block.q25 === '' ? null : toNumber(block.q25);
  const q50Count =
    block.q50 == null || block.q50 === '' ? null : toNumber(block.q50);
  const q75Count =
    block.q75 == null || block.q75 === '' ? null : toNumber(block.q75);
  const q100Count =
    block.q100 == null || block.q100 === '' ? null : toNumber(block.q100);

  return {
    start: formatTime(block.start),
    end: formatTime(block.end),
    clips: joinClips(block.clips),
    impressions,
    q25Count,
    q50Count,
    q75Count,
    q100Count,
    impressionsDisplay: numberFormatter.format(impressions),
    q25Display: q25Count == null ? '—' : numberFormatter.format(q25Count),
    q50Display: q50Count == null ? '—' : numberFormatter.format(q50Count),
    q75Display: q75Count == null ? '—' : numberFormatter.format(q75Count),
    q100Display: q100Count == null ? '—' : numberFormatter.format(q100Count),
  };
}

function formatTime(value) {
  return String(value || '').slice(0, 8);
}

function joinClips(clips) {
  if (!Array.isArray(clips) || !clips.length) return '—';
  return clips.join('\n');
}

function toNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 0;
  return num;
}

function resolveLocaleForFormatter(lang = 'uk') {
  const map = {
    uk: 'uk-UA',
    en: 'en-US',
    it: 'it-IT',
    fr: 'fr-FR',
  };
  const normalized = String(lang || '').toLowerCase();
  if (map[normalized]) return map[normalized];
  const prefix = normalized.split('-')[0];
  return map[prefix] || 'en-US';
}

function buildFormatter(locale, options) {
  const fallback = 'en-US';
  try {
    return new Intl.NumberFormat(locale || fallback, options);
  } catch {
    return new Intl.NumberFormat(fallback, options);
  }
}

function createFormatters(locale = 'uk-UA') {
  return {
    numberFormatter: buildFormatter(locale),
    percentFormatter: buildFormatter(locale, {
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }),
  };
}

function parseTimeToSeconds(value) {
  if (!value) return null;
  const str = String(value).trim();
  const [h = '0', m = '0', rest = '0'] = str.split(':');
  const [s = '0'] = rest.split('.');
  const hh = Number(h) || 0;
  const mm = Number(m) || 0;
  const ss = Number(s) || 0;
  return hh * 3600 + mm * 60 + ss;
}

function isNewAdBlock(prevRow, currentRow) {
  if (!prevRow || !currentRow) return false;
  const prevEndSec = parseTimeToSeconds(prevRow.end);
  const currStartSec = parseTimeToSeconds(currentRow.start);
  if (prevEndSec == null || currStartSec == null) return false;

  // Внутри одного рекламного блока ролики идут вплотную: start == prev end.
  // Если есть зазор (start > prev end), считаем, что начался новый блок.
  return currStartSec > prevEndSec;
}

function buildFilenameBase(prefix, shortcode, date) {
  const clean = (val) =>
    String(val || '')
      .trim()
      .replace(/\s+/g, '_')
      .replace(/[^0-9A-Za-z_-]/g, '')
      .toLowerCase();
  const channel = clean(shortcode || 'channel');
  const safeDate = clean(date || '');
  return [prefix, channel, safeDate].filter(Boolean).join('_');
}

function maskIp(ip) {
  if (!ip) return '';
  const str = String(ip);

  let digitsSeen = 0;
  let result = '';

  // идём с конца строки и заменяем последние 3 цифры на '*'
  for (let i = str.length - 1; i >= 0; i--) {
    const ch = str[i];
    if (/\d/.test(ch) && digitsSeen < 3) {
      result = '*' + result;
      digitsSeen += 1;
    } else {
      result = ch + result;
    }
  }

  return result;
}
